'use client';

import { create } from 'zustand';
import type { AuthClientResponse } from '@sola/schema/dotcom/auth.ts';
import { AuthScenario } from '@sola/schema/dotcom/auth.ts';
import type { AccountCreatePayload } from '@sola/schema/dotcom/auth/account.ts';
import { AuthParamReview, AuthRedirectPage, AuthRedirectPagePaths, AuthSearchParam, AuthSessionExpirationReason } from '@sola/schema/dotcom/auth/routes.ts';
import { SupporterPostBaseSchema } from '@sola/schema/dotcom/proxy-api/supporter-schema.ts';
import type { SupporterPostBase } from '@sola/schema/dotcom/proxy-api/supporter.ts';
import { getAuthClientCookie, updateAuthClientCookie } from '@sola/shared-utils/auth/frontendCookie.ts';
import { hasAuth } from '@sola/shared-utils/frontend-sessions.ts';
import { getEnvironmentRoot } from '@sola/shared-utils/misc.ts';
import { myPagePaths } from '@sola/shared-utils/my/routes.ts';
import { type ZustandSelectedSlices, type ZustandSliceKey, useZustandSlices } from '@sola/shared-utils/react.tsx';
import type { AuthAppConfig } from './apps.ts';
import { APPROVED_APPS } from './apps.ts';
import { accountCreate, authenticateUser, confirmAccountEmail, forgotPassword, forgotPasswordValidate, logoutUser, recoverUsername, resendConfirmationEmail, signup, signupValidate, updatePassword as updatePasswordRequest, updatePreferredUsername as updatePreferredUsernameRequest, userCreate, userCreateFromAccount, verifyUser } from './requests.ts';
import { defaultAuthRedirectUrl, defaultMyRedirectUrl, isTrustedRedirect } from './utils.ts';
export enum AuthStatusEnum {
  Authenticated = 'AUTHENTICATED',
  LoggedOut = 'LOGGED_OUT',
  AccountCreated = 'ACCOUNT_CREATED',
  AccountCreatedAndAuthenticated = 'ACCOUNT_CREATED_AND_AUTHENTICATED',
  AccountCreatedConfirmed = 'ACCOUNT_CREATED_CONFIRMED',
  InProgress = 'IN_PROGRESS',
  Success = 'SUCCESS',
}
interface AuthError {
  type: AuthScenario;
  message: string;
}
interface AuthActions {
  checkIfUserIsAuthenticated: () => Promise<void>;
  logIn: (username: string, password: string, includeSupporter?: boolean) => Promise<AuthClientResponse | null>;
  logOut: () => Promise<void>;
  userCreateFromAccount: (globalId: string, postalCode: string, username: string, password: string, email: string) => Promise<void>;
  userCreate: (username: string, password: string) => Promise<void>;
  accountCreate: (args: SupporterPostBase) => Promise<AuthRedirectPage | null>;
  resendConfirmationEmail: (args: {
    authUsername?: string;
    email?: string;
  }) => Promise<void>;
  signupValidate: (code: string) => Promise<AuthRedirectPage | null>;
  forgotPassword: (email: string, username?: string, sponsorDonorNumber?: string) => Promise<void>;
  forgotPasswordValidate: (authUsername: string, validationCode: string, newPassword: string) => Promise<void>;
  confirmEmail: (authUsername: string, validationCode: string) => Promise<void>;
  signup: (email: string) => Promise<void>;
  recoverUsername: (email: string, sponsorDonorNumber?: string) => Promise<void>;
  getReturnLink: () => string;
  getError: () => AuthError | null;
  clearError: () => void;
  getCurrentStatus: () => AuthStatusEnum | null;
  updateUsername: (preferredUsername: string) => Promise<string | null>;
  updatePassword: (password: string) => Promise<void>;
}
interface FormState {
  email?: string;
}
export interface AuthState {
  actions: AuthActions;
  appConfig: AuthAppConfig | null;
  authUser?: AuthClientResponse | null;
  currentStatus: AuthStatusEnum;
  error: AuthError | null;
  formState?: FormState;
  isReady: boolean;
  isLoggedIn: () => boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  returnUrl: URL | null;
  /** Transient flag from accountCreate/signupValidate; drives query-param append in getReturnLink. Not persisted in Redis. */
  reviewAccountInfo: boolean;
  sessionExpired: boolean;
  createPath: string;
  sessionId?: string;
}

/**
 * Internal hook to create the zustand store.
 */
export const useAuthStore = create<AuthState>()((set, get) => {
  return {
    appConfig: null,
    authUser: null,
    currentStatus: AuthStatusEnum.InProgress,
    error: null,
    returnUrl: null,
    reviewAccountInfo: false,
    sessionExpired: false,
    createPath: process.env.NEXT_PUBLIC_FF_USE_SIGNUP === 'true' ? AuthRedirectPagePaths[AuthRedirectPage.Signup] : AuthRedirectPagePaths[AuthRedirectPage.Create],
    isReady: false,
    isLoggedIn: () => {
      const {
        currentStatus
      } = get();
      return currentStatus === AuthStatusEnum.Authenticated || currentStatus === AuthStatusEnum.Success;
    },
    isLoggingIn: false,
    isLoggingOut: false,
    actions: {
      // TODO: Even if the user is not authenticated, there might be an authuser object present because logout would never have been called. Should we clear out the auth user object everytime we run init?
      checkIfUserIsAuthenticated: async () => {
        try {
          // Set status to InProgress
          set({
            currentStatus: AuthStatusEnum.InProgress
          });

          // Run /verify api call.
          const verifyResponse = await verifyUser();
          if (verifyResponse && verifyResponse.success) {
            // Set status to LOGGED_IN
            // Set authUser to auth object.
            set({
              authUser: verifyResponse,
              currentStatus: AuthStatusEnum.Authenticated,
              sessionExpired: false
            });
          } else {
            // If error or not authenticated set status to LOGGED_OUT
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null
            });
          }
        } catch {
          // if error, set status to logged_out
          // set errors here or return error?
          const authState = get();
          set({
            error: {
              type: AuthScenario.NOT_AUTHORIZED,
              message: authState.error?.message || ''
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
        set({
          isReady: true
        });
      },
      logIn: async (username, password, includeSupporter) => {
        // if username and password not provided return error.
        if (!username || !password) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Missing username or password'
            }
          });
          return null;
        }
        // set status to logging_in
        set({
          error: null,
          isLoggingIn: true,
          currentStatus: AuthStatusEnum.InProgress
        });
        try {
          const response = await authenticateUser({
            username,
            password,
            includeSupporter
          });
          if (response && response.success) {
            set({
              authUser: response,
              currentStatus: AuthStatusEnum.Authenticated,
              isLoggingIn: false,
              sessionExpired: false
            });
            return response;
          }
          if (response && !response.success) {
            if (response.type === AuthScenario.REQUIRED_EMAIL_VERIFICATION) {
              set({
                // TODO: Isn't this authUser just reset to null below?
                authUser: {
                  success: true,
                  authUsername: username,
                  authUserGlobalId: '',
                  authUserInternalId: '',
                  authUserPrimaryContactId: ''
                }
              });
            }
            set({
              error: {
                type: response.type,
                message: response.message
              },
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null
            });
          }
        } catch (error: unknown) {
          console.error('Unexpected error:', error);
          set({
            error: {
              message: 'Unexpected error',
              type: AuthScenario.INTERNAL_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
        return null;
      },
      logOut: async () => {
        try {
          // set status to logging_in
          set({
            currentStatus: AuthStatusEnum.InProgress,
            authUser: null,
            error: null,
            isLoggingOut: true
          });

          // then, run /authenticate
          const results = await logoutUser();

          // if succesful, set status to authenticated
          if (results) {
            // set authuser to auth object
            set({
              authUser: null,
              currentStatus: AuthStatusEnum.LoggedOut,
              isLoggingOut: false,
              sessionExpired: false
            });
          }
        } catch {
          // if error, set status to logged_out
          // set errors here or return error?
          // todo: add-error util
          const authState = get();
          // const newError: AuthError = {
          //   // type: CheckoutErrorType.Bad,
          //   message: error.message,
          // };
          set({
            error: authState.error,
            // errors: [...errors, newError],
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null
          });
        }
      },
      userCreateFromAccount: async (globalId: string, postalCode: string, username: string, password: string, email: string) => {
        // If email is provided, there is no need for global id and postal code
        if (!username || !password || globalId && !postalCode || !postalCode && !globalId && !email) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Please fill in all fields.'
            }
          });
          return;
        }
        set({
          error: null,
          currentStatus: AuthStatusEnum.InProgress,
          formState: {
            email
          },
          authUser: {
            success: true,
            authUsername: globalId || username,
            authUserGlobalId: globalId || '',
            authUserInternalId: '',
            authUserPrimaryContactId: ''
          }
        });
        const {
          appConfig,
          returnUrl
        } = useAuthStore.getState();
        try {
          const response = await userCreateFromAccount({
            globalId,
            postalCode,
            username,
            password,
            email,
            app: appConfig?.key,
            returnUrl: returnUrl?.toString()
          });
          if (!response) {
            // this will either be an error scenario or not a scenario at all
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: 'Unknown error',
                type: AuthScenario.UNKNOWN_ERROR
              }
            });
            return;
          }
          if (response.success) {
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED_AND_AUTHENTICATED && response.sessionJwt) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedAndAuthenticated
              });
              return;
            }
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedConfirmed
              });
              return;
            }
            set({
              authUser: {
                success: true,
                authUsername: globalId || username,
                authUserGlobalId: globalId || '',
                authUserInternalId: '',
                authUserPrimaryContactId: ''
              },
              formState: {
                email
              },
              currentStatus: AuthStatusEnum.AccountCreated
            });
          } else {
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: response?.message || 'Unknown error',
                type: response?.type || AuthScenario.UNKNOWN_ERROR
              }
            });
          }
        } catch (error) {
          console.error('Unexpected error:', error);
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null,
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      userCreate: async (username, password) => {
        if (!username || !password) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Please fill in all fields.'
            }
          });
          return;
        }
        const {
          sessionId
        } = get();
        if (!sessionId) {
          set({
            error: {
              type: AuthScenario.INVALID,
              message: 'Invalid Session Id'
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return;
        }
        set({
          error: null,
          currentStatus: AuthStatusEnum.InProgress
        });
        try {
          const response = await userCreate({
            username,
            password,
            sessionId
          });
          if (!response) {
            // this will either be an error scenario or not a scenario at all
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: 'Unknown error',
                type: AuthScenario.UNKNOWN_ERROR
              }
            });
            return;
          }
          if (response.success) {
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED_AND_AUTHENTICATED && response.sessionJwt) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedAndAuthenticated
              });
              return;
            }
            if (response.type === AuthScenario.ACCOUNT_CONFIRMED) {
              set({
                authUser: response,
                currentStatus: AuthStatusEnum.AccountCreatedConfirmed
              });
              return;
            }
            set({
              currentStatus: AuthStatusEnum.AccountCreated
            });
          } else {
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: response?.message || 'Unknown error',
                type: response?.type || AuthScenario.UNKNOWN_ERROR
              }
            });
          }
        } catch (error) {
          console.error('Unexpected error:', error);
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null,
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      accountCreate: async (args: SupporterPostBase) => {
        const parsed = SupporterPostBaseSchema.safeParse(args);
        if (!parsed.success) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Please fill in all fields.'
            }
          });
          return null;
        }
        const {
          sessionId
        } = get();
        if (!sessionId) {
          set({
            error: {
              type: AuthScenario.INVALID,
              message: 'Invalid session Id'
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return null;
        }
        set({
          error: null,
          currentStatus: AuthStatusEnum.InProgress
        });
        try {
          const response = await accountCreate({
            sessionId,
            ...args
          } as AccountCreatePayload);
          if (!response) {
            // this will either be an error scenario or not a scenario at all
            set({
              currentStatus: AuthStatusEnum.LoggedOut,
              authUser: null,
              error: {
                message: 'Unknown error',
                type: AuthScenario.UNKNOWN_ERROR
              }
            });
            return null;
          }
          if (response.success) {
            if (!response.nextStep || !(response.nextStep in AuthRedirectPagePaths)) {
              set({
                error: {
                  type: AuthScenario.INVALID,
                  message: 'Invalid Session'
                }
              });
              return null;
            }
            set({
              currentStatus: AuthStatusEnum.AccountCreated,
              reviewAccountInfo: response.accountFoundMidSignup === true
            });
            return response.nextStep;
          }
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null,
            error: {
              message: response?.message || 'Unknown error',
              type: response?.type || AuthScenario.UNKNOWN_ERROR
            }
          });
          return null;
        } catch {
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null,
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
          return null;
        }
      },
      signup: async email => {
        if (!email) {
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.UNKNOWN_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return;
        }
        const {
          appConfig,
          returnUrl
        } = get();
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        try {
          const response = await signup(email, appConfig?.key, returnUrl?.toString());
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else {
            set({
              error: null,
              currentStatus: AuthStatusEnum.Success,
              // TODO: I'm thinking this isn't needed. If we are abandoning the signup flow with the check email
              // then the user always clicks the link and we validate their link we are going to redirect them to
              // the next steps will not be using this same auth state.
              formState: {
                email
              }
            });
          }
        } catch {
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
      },
      confirmEmail: async (authUsername, confirmationCode: string) => {
        if (!authUsername) {
          // TODO: if we don't have the username either in the authUsernamParam or the state should we give them a better message:
          // to re-click the link and if you continue to have issues call this number.. our number.
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.UNKNOWN_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        try {
          const response = await confirmAccountEmail({
            confirmationCode,
            authUsername
          });
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else {
            set({
              error: null,
              currentStatus: AuthStatusEnum.AccountCreatedConfirmed
            });
          }
        } catch {
          const {
            error: stateError
          } = get();
          set({
            error: {
              message: stateError?.message || 'Unknown error',
              type: stateError?.type || AuthScenario.INTERNAL_ERROR
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
      },
      resendConfirmationEmail: async (args: {
        authUsername?: string;
        email?: string;
      }) => {
        const {
          authUsername,
          email
        } = args;
        const {
          authUser
        } = get();
        const currentUsername = authUsername || authUser?.authUsername || undefined;
        if (!currentUsername && !email) {
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            error: {
              message: 'Missing username',
              type: AuthScenario.MISSING_FIELDS
            }
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        const {
          appConfig,
          returnUrl
        } = useAuthStore.getState();
        try {
          const response = await resendConfirmationEmail({
            authUsername: currentUsername,
            email,
            app: appConfig?.key,
            returnUrl: returnUrl?.toString()
          });
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else {
            const responseUsername = currentUsername || response?.authUsername;
            if (!responseUsername) {
              set({
                error: {
                  type: AuthScenario.INTERNAL_ERROR,
                  message: 'Unknown error'
                }
              });
              return;
            }
            set({
              authUser: {
                success: true,
                authUsername: responseUsername,
                authUserGlobalId: '',
                authUserInternalId: '',
                authUserPrimaryContactId: ''
              },
              formState: {
                email
              },
              currentStatus: AuthStatusEnum.Success
            });
          }
        } catch {
          // TODO: These caught errors need cleaning up and reasoning through...
          // Is the error coming from the catch or the state?
          const {
            error: stateError
          } = get();
          const newError: AuthError = {
            message: stateError?.message || 'Unknown error',
            type: stateError?.type || AuthScenario.INTERNAL_ERROR
          };
          set({
            error: newError,
            currentStatus: AuthStatusEnum.LoggedOut
          });
        }
      },
      signupValidate: async code => {
        const {
          sessionId
        } = get();
        if (!code) {
          set({
            error: {
              message: 'Missing params',
              type: AuthScenario.MISSING_FIELDS
            }
          });
          return null; // TODO: Should the store determine next step to signup?
        }
        if (!sessionId) {
          set({
            error: {
              message: 'Missing params',
              type: AuthScenario.INVALID
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return null; // TODO: Should the store determine next step to signup?
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        try {
          const response = await signupValidate(sessionId, code);
          if (!response) {
            set({
              error: {
                type: AuthScenario.INTERNAL_ERROR,
                message: 'Unknown error'
              }
            });
            return null; // TODO: Should the store determine next step to signup?
          }
          if (!response.success) {
            set({
              error: {
                type: AuthScenario.INVALID_CODE,
                message: 'Invalid Code'
              }
            });
            return null; // TODO: Should the store determine next step to signup?
          }
          if (!response.nextStep || !(response.nextStep in AuthRedirectPagePaths)) {
            set({
              error: {
                type: AuthScenario.INVALID,
                message: 'Invalid Session'
              }
            });
            return null; // TODO: Should the store determine next step to signup?
          }
          set({
            formState: {
              email: response.email
            },
            currentStatus: AuthStatusEnum.Success,
            reviewAccountInfo: response.accountFoundMidSignup === true
          });
          return response.nextStep;
        } catch {
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
          return null;
        }
      },
      forgotPassword: async (email: string, username?: string, globalId?: string) => {
        if (!email) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Missing email'
            }
          });
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          authUser: null,
          error: null
        });
        const {
          appConfig,
          returnUrl
        } = useAuthStore.getState();
        try {
          const response = await forgotPassword({
            email,
            username,
            globalId,
            app: appConfig?.key,
            returnUrl: returnUrl?.toString()
          });
          if (response && !response.success) {
            set({
              formState: {
                email
              },
              error: {
                type: response.type,
                message: response.message
              }
            });
          } else if (response) {
            set({
              authUser: {
                success: true,
                authUsername: response?.authUsername,
                authUserGlobalId: '',
                authUserInternalId: '',
                authUserPrimaryContactId: ''
              }
            });
          }
        } catch {
          const {
            error: stateError
          } = get();
          set({
            error: {
              message: stateError?.message || 'Unknown error',
              type: stateError?.type || AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      forgotPasswordValidate: async (authUsername: string, validationCode: string, password: string) => {
        try {
          if (!authUsername || !validationCode || !password) {
            set({
              error: {
                type: AuthScenario.MISSING_FIELDS,
                message: 'Missing email'
              }
            });
          }
          set({
            currentStatus: AuthStatusEnum.InProgress,
            error: null
          });
          const response = await forgotPasswordValidate({
            authUsername,
            validationCode,
            password
          });
          if (response && !response.success) {
            set({
              error: {
                type: response.type,
                message: response.message
              }
            });
          }
        } catch {
          const {
            error: stateError
          } = get();
          set({
            error: {
              message: stateError?.message || 'Unknown error',
              type: stateError?.type || AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      recoverUsername: async (email: string, sponsorDonorNumber?: string) => {
        if (!email) {
          set({
            error: {
              message: 'Missing email',
              type: AuthScenario.MISSING_FIELDS
            },
            currentStatus: AuthStatusEnum.LoggedOut
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          authUser: null,
          error: null
        });
        try {
          const response = await recoverUsername({
            email,
            sponsorDonorNumber
          });
          if (response && !response.success) {
            set({
              formState: {
                email
              },
              error: {
                type: response.type,
                message: response.message
              }
            });
          }
        } catch {
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      },
      getReturnLink: () => {
        const {
          authUser,
          appConfig,
          returnUrl,
          reviewAccountInfo
        } = get();
        let redirectUrl = new URL(returnUrl ?? defaultAuthRedirectUrl);
        if (reviewAccountInfo) {
          if (!appConfig) {
            redirectUrl = new URL(myPagePaths.accountSettings, defaultMyRedirectUrl);
          }
          redirectUrl.searchParams.set(AuthSearchParam.Review, AuthParamReview.AccountInfo);
        }
        if (appConfig?.domain && authUser?.sessionJwt) {
          redirectUrl.searchParams.set(AuthSearchParam.Jwt, authUser.sessionJwt);
        }
        return redirectUrl.href;
      },
      getError: () => {
        const {
          error
        } = get();
        return error;
      },
      clearError: () => {
        set({
          error: null
        });
      },
      getCurrentStatus: () => {
        const {
          currentStatus
        } = get();
        return currentStatus;
      },
      updateUsername: async (preferredUsername: string) => {
        const {
          authUser
        } = get();
        const authUsername = authUser?.authUsername;
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        if (!authUsername) {
          set({
            currentStatus: AuthStatusEnum.LoggedOut,
            authUser: null
          });
          // TODO: how will we handle not authorized from the store
          return null;
        }
        try {
          const response = await updatePreferredUsernameRequest(authUsername, preferredUsername);
          if (response?.success) {
            set({
              currentStatus: AuthStatusEnum.Success
            });
            return response.preferredUsername;
          }
          set({
            error: {
              type: response?.type || AuthScenario.INTERNAL_ERROR,
              message: response?.message || 'Unknown error'
            }
          });
          return null;
        } catch {
          set({
            error: {
              message: 'Unknown error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
          return null;
        }
      },
      updatePassword: async (password: string) => {
        if (!password) {
          set({
            error: {
              type: AuthScenario.MISSING_FIELDS,
              message: 'Missing password'
            }
          });
          return;
        }
        const {
          authUser
        } = get();
        if (!authUser?.authUsername) {
          set({
            error: {
              message: 'User is not authenticated',
              type: AuthScenario.NOT_AUTHORIZED
            }
          });
          return;
        }
        set({
          currentStatus: AuthStatusEnum.InProgress,
          error: null
        });
        try {
          const response = await updatePasswordRequest(authUser.authUsername, password);
          if (!response?.success) {
            set({
              error: {
                type: response?.type ?? AuthScenario.INTERNAL_ERROR,
                message: response?.message ?? 'Unexpected error'
              }
            });
            return;
          }
          set({
            currentStatus: AuthStatusEnum.Success,
            error: null
          });
        } catch {
          set({
            error: {
              message: 'Unexpected error',
              type: AuthScenario.INTERNAL_ERROR
            }
          });
        }
      }
    }
  };
});

/*
 * Hook that allows consumers to easily implement slices on auth store.
 */
export const useAuth = <TKeys extends readonly ZustandSliceKey<AuthState>[],>(...props: TKeys): ZustandSelectedSlices<AuthState, TKeys> => {
  return useZustandSlices<AuthState, TKeys>(useAuthStore, ...props);
};

/**
 * Function to initialize auth store.
 */
export const initAuth = async (isAuthCompassion: boolean) => {
  if (isAuthCompassion) {
    const appCookies = getAuthClientCookie();
    const url = new URL(globalThis.location.href);
    if (!url.search && appCookies) {
      url.search = appCookies;
    }
    // Not using useSearchParams to prevent needing Suspense boundaries
    const app = url.searchParams.get(AuthSearchParam.App);
    // When coming to auth, the public contract is "redirect" but internally we store and refer to it as "returnUrl"...
    const returnQuery = url.searchParams.get(AuthSearchParam.Redirect);
    const sessionExpired = url.searchParams.get(AuthSearchParam.Expired) === AuthSessionExpirationReason.Idle;
    const sessionId = url.searchParams.get(AuthSearchParam.SessionId);
    const appConfig = app && Object.keys(APPROVED_APPS).includes(app) ? APPROVED_APPS[app] : null;
    const envDomain = getEnvironmentRoot(defaultAuthRedirectUrl);
    const domain = appConfig?.domain || envDomain;
    const returnUrl = returnQuery && URL.canParse(returnQuery) ? new URL(returnQuery) : null;
    const verifiedReturnUrl = isTrustedRedirect(returnUrl, domain) ? returnUrl : new URL(defaultAuthRedirectUrl);
    useAuthStore.setState({
      returnUrl: verifiedReturnUrl,
      sessionExpired,
      ...(sessionId && {
        sessionId
      }),
      ...(appConfig?.useSignup && {
        createPath: AuthRedirectPagePaths[AuthRedirectPage.Signup]
      })
    });
    const persistentSearchParams = new URLSearchParams(url.searchParams);
    persistentSearchParams.delete(AuthSearchParam.Expired);
    const persistentSearch = persistentSearchParams.toString();
    if (persistentSearch && (app || returnUrl)) {
      updateAuthClientCookie(`?${persistentSearch}`);
    }
    if (appConfig) {
      useAuthStore.setState({
        appConfig
      });
    }
  }

  // Procceed if have no auth cookie.
  if (!hasAuth()) {
    useAuthStore.setState({
      currentStatus: AuthStatusEnum.LoggedOut,
      isReady: true
    });
    return;
  }
  const {
    actions
  } = useAuthStore.getState();
  await actions.checkIfUserIsAuthenticated();
};